import { type ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type { VpError } from '../../../generated/internal.graphql';
import * as VPError from '../../../VPError';
import { type InternalApiError, toApiErrors } from '../errors';
import type { VPResult } from '../../VPResult';
import { CONNECTION_METADATA_FIELDS, type ConnectionArguments } from '../../connection/Api';
import * as GqlConnection from '../../connection/GqlConnection';
import * as Ordering from '../../Shared/Ordering';
import * as Order from './Order';
import * as OrderDetail from './OrderDetail';
import type { OrderSearchArguments } from './OrderSearchArguments';
import { INTERNAL_ORDER_DETAIL_FIELDS, INTERNAL_ORDER_SUMMARY_FIELDS } from './fragments';
import { ACCOUNT_AGNOSTIC, forAccount } from '../../accountContext';

/**
 * Staff-only, cross-account order reads and interventions. These live on the internal (VP staff)
 * schema rather than AppSchema: they resolve any order regardless of account, which makes them
 * the sharpest escalation risk to leave in the published schema. Auth is the superadmin session
 * cookie — a bearer token never satisfies the gate — so everything here goes through
 * `internalGqlApiClient`.
 *
 * Neither mutation payload carries an `order`. A caller that needs refreshed state refetches it —
 * and for a **cross-account** order that refetch must come from `fetchOrder` below, not from
 * AppSchema, which cannot see an order outside the caller's own account.
 *
 * A non-superadmin caller is rejected with HTTP 401 before the query is parsed, which Apollo
 * throws rather than returning a payload — so every function here catches and classifies via
 * `toApiErrors`, and callers can branch on `isUnauthorized` to say "VenuePilot staff only"
 * instead of surfacing a generic failure (or reporting an expected outcome to Sentry).
 */

const INTERNAL_ORDERS_QUERY = gql`
  query InternalOrders($after: String, $before: String, $first: Int, $last: Int, $filter: OrderSearchArguments, $sort: [OrderingArgument!]) {
    orders(after: $after, before: $before, first: $first, last: $last, filter: $filter, sort: $sort) {
      edges {
        cursor
        node {
          ...InternalOrderSummaryFields
        }
      }
      pageInfo {
        ...ConnectionMetadataFields
      }
    }
  }
  ${INTERNAL_ORDER_SUMMARY_FIELDS}
  ${CONNECTION_METADATA_FIELDS}
`;

const INTERNAL_ORDER_QUERY = gql`
  query InternalOrder($id: Int!) {
    order(id: $id) {
      ...InternalOrderDetailFields
    }
  }
  ${INTERNAL_ORDER_DETAIL_FIELDS}
`;

/**
 * `allAccounts` defaults to false server-side, takes precedence over `accountId` when true, and
 * the query is **rejected** when it is false with no `accountId` — so it has to be derived here
 * rather than left to callers.
 *
 * Three cases, not two. `AdminOrderIndex` builds its account id with `Number(route.query.…)`, so a
 * hand-edited `?accountId=abc` arrives as `NaN`. Deriving from truthiness (`!accountId`) would
 * read that as "no account given" and widen to every account's orders — the exact escalation this
 * schema exists to remove. A present-but-unusable id therefore fails closed: no `accountId`,
 * `allAccounts` still false, and the backend rejects the unscoped read.
 */
const toGqlFilter = (filter?: OrderSearchArguments) => {
  const requested = filter?.accountId !== undefined;
  const usable = Number.isInteger(filter?.accountId) && filter!.accountId! > 0;

  return {
    ...(filter ?? {}),
    accountId: usable ? filter!.accountId : undefined,
    allAccounts: !requested,
  };
};

/**
 * The account the read runs in, from the same filter so the header and `allAccounts` cannot
 * disagree. The middle branch is why there are three: an id `forAccount` could not deliver — `NaN`,
 * or one `accountMiddleware`'s truthiness check would drop, such as `0` — names nothing rather than
 * claiming an account the request would not carry, and declaring it agnostic would widen the read
 * `toGqlFilter` already failed closed on the same test.
 */
const filterContext = (filter?: OrderSearchArguments) => {
  const accountId = filter?.accountId;

  if (Number.isInteger(accountId) && accountId! > 0) {
    return forAccount(accountId!);
  }

  if (accountId !== undefined) {
    return undefined;
  }

  return ACCOUNT_AGNOSTIC;
};

export const fetchOrders = async (
  client: ApolloClient<NormalizedCacheObject>,
  {
    pagination,
    filter,
    sort,
  }: { pagination?: ConnectionArguments; filter?: OrderSearchArguments; sort?: Ordering.Ordering<'created_at'>[] },
): Promise<VPResult<GqlConnection.GqlConnection<Order.Order>, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_ORDERS_QUERY,
      fetchPolicy: 'network-only',
      variables: {
        ...(pagination ?? {}),
        filter: toGqlFilter(filter),
        sort: sort ?? [],
      },
      context: filterContext(filter),
    });

    return { success: true, data: GqlConnection.fromGql(data.orders, Order.fromGql) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

export const fetchOrder = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<VPResult<OrderDetail.OrderDetail | undefined, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_ORDER_QUERY,
      fetchPolicy: 'network-only',
      variables: { id },
      // Account-agnostic: the schema resolves `order(id)` without account scoping, so a globally
      // unique id is the whole address.
      context: ACCOUNT_AGNOSTIC,
    });

    // A missing order is an answer (bad id), not a failure — don't dress it up as one.
    return { success: true, data: data?.order ? OrderDetail.fromGql(data.order) : undefined };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

const MAKE_TICKET_UNCLAIMABLE_MUTATION = gql`
  mutation InternalMakeTicketUnclaimable($ticketId: Int!) {
    makeTicketUnclaimable(input: { ticketId: $ticketId }) {
      success
      errors {
        message
        path
      }
    }
  }
`;

const MANUALLY_COMPLETE_ORDER_MUTATION = gql`
  mutation InternalManuallyCompleteOrder($orderId: Int!, $paymentIntentId: String!) {
    manuallyCompleteOrder(input: { orderId: $orderId, paymentIntentId: $paymentIntentId }) {
      success
      errors {
        message
        path
      }
    }
  }
`;

export type MutationResult = { errors: InternalApiError[]; success: boolean };

const toResult = (payload?: { success?: boolean; errors?: VpError[] | null }): MutationResult => ({
  errors: payload?.success ? [] : VPError.fromGqlCollection(payload?.errors ?? []),
  success: !!payload?.success,
});

export const makeTicketUnclaimable = async (
  client: ApolloClient<NormalizedCacheObject>,
  ticketId: number,
): Promise<MutationResult> => {
  try {
    const { data } = await client.mutate({
      mutation: MAKE_TICKET_UNCLAIMABLE_MUTATION,
      variables: { ticketId },
      fetchPolicy: 'network-only',
      // Account-agnostic: the SDL marks it "(VP staff, cross-account)"; a global ticket id addresses it.
      context: ACCOUNT_AGNOSTIC,
    });

    return toResult(data?.makeTicketUnclaimable);
  } catch (error) {
    return { errors: toApiErrors(error), success: false };
  }
};

export const manuallyCompleteOrder = async (
  client: ApolloClient<NormalizedCacheObject>,
  orderId: number,
  paymentIntentId: string,
): Promise<MutationResult> => {
  try {
    const { data } = await client.mutate({
      mutation: MANUALLY_COMPLETE_ORDER_MUTATION,
      variables: { orderId, paymentIntentId },
      fetchPolicy: 'network-only',
      // Account-agnostic: likewise "(VP staff, cross-account)", addressed by a global order id.
      context: ACCOUNT_AGNOSTIC,
    });

    return toResult(data?.manuallyCompleteOrder);
  } catch (error) {
    return { errors: toApiErrors(error), success: false };
  }
};
